import { TextField, Typography, Grid } from "@mui/material";
import ProductSelector, { SelectedProductDetails } from "domains/products/components/product-selector/ProductSelector";
import { useCallback, useState } from "react";
import localisations from "../../../localisation";
import { ManagedProductStatus } from "domains/allocations/components/managed-product-status";
import allocationLocalisations from "domains/allocations/localisation";
import { SelectedPartitionDetails } from "domains/partitions/components/partition-selector/PartitionSelector";
import { ProductQuantityStatus } from "domains/allocations/components/product-quantity-status";
import { QuerySnapshotResponse } from "domains/allocations/services/allocation-service";

type SelectProductAndQuantityProps = {
    fieldCompanyId: number;
    fields: {
        parentHierarchyNodeId?: number | null;
        expandedNodes: string[];
        productDetails?: SelectedProductDetails;
        quantityChange: number;
        notes: string;
        partitionDetails: SelectedPartitionDetails,
    };
    handleFieldChange: (fields: any) => void;
    handleAdjustProductStatus: (status: boolean) => void;
    handleProductQuantityStatus: (status: boolean) => void;
};

export default function SelectProductAndQuantityStage(props: SelectProductAndQuantityProps) {
    const {
        heading,
        blurb,
        quantityLabel,
        quantityHelperText,
        notesLabel,
        notesHelperText
    } = localisations.adjustmentAllocation.stages.selectProductAndQuantity;
    const { fields, handleFieldChange } = props;
    const [quantityStr, setQuantityStr] = useState((fields.quantityChange ?? 0).toString());
    const [productRefresh, setProductRefresh] = useState(false);
    const [isManagedProduct, setManagedProduct] = useState(false);

    const onProductQuantityChanged = useCallback((event) => {
        let quantity = event.target.value;
        setQuantityStr(quantity);
        props.handleProductQuantityStatus(quantity !== '0');
        handleFieldChange({ quantityChange: parseInt(quantity, 10) });
    }, [handleFieldChange, props]);

    const onProductStatusLoaded = useCallback((status) => {
        setProductRefresh(false);
        setManagedProduct(status);
        props.handleAdjustProductStatus(status);
    }, [props]);

    const validateStock = (data: QuerySnapshotResponse): boolean => {
        let item = data.items.find(i => i.productId === fields.productDetails?.id);
        let isValidStock = (fields.quantityChange === null || fields.quantityChange === undefined || fields.quantityChange === 0)
            ? false
            : item === undefined ? fields.quantityChange > 0 : fields.quantityChange + item.quantity >= 0;
        return isValidStock && isManagedProduct;
    }

    const onProductQuantityStatusLoaded = useCallback((status) => {
        props.handleProductQuantityStatus(status);
    }, [props]);

    const formatMessageQuantity = (status: boolean): string =>
        fields.quantityChange === 0 || Number.isNaN(fields.quantityChange)
            ? allocationLocalisations.adjustStock.zeroQuantityStatus
            : isManagedProduct
                ? status ? allocationLocalisations.adjustStock.sufficientQuantityStatus : allocationLocalisations.adjustStock.negativeQuantityStatus
                : allocationLocalisations.adjustStock.insufficientQuantityStatus;


    return (
        <>
            <Typography mb={4} variant="h5" gutterBottom>
                {heading}
            </Typography>

            <Typography variant="body1" gutterBottom>
                {blurb}
            </Typography>

            <Grid container rowSpacing={8}>
                <Grid item xs={12}>
                    <ProductSelector
                        fieldCompanyId={props.fieldCompanyId}
                        initialProductId={fields.productDetails?.id}
                        onSelected={useCallback((productDetails) => handleFieldChange({ productDetails: productDetails }), [handleFieldChange])}
                    />

                    <ManagedProductStatus
                        productId={fields.productDetails?.id ?? null}
                        onLoaded={onProductStatusLoaded}
                        forceRefresh={productRefresh}
                        formatMessage={(status) => status ? allocationLocalisations.managedProductStatus.managed : allocationLocalisations.managedProductStatus.unmanaged}
                    />
                </Grid>

                <Grid item xs={12}>
                    <TextField
                        id="quantity-field"
                        placeholder="Quantity"
                        label={quantityLabel}
                        helperText={quantityHelperText}
                        fullWidth
                        value={quantityStr}
                        type="number"
                        onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                            onProductQuantityChanged(event)
                        }}
                    />

                    <ProductQuantityStatus
                        validStock={validateStock}
                        productId={fields.productDetails?.id ?? null}
                        partitionId={fields.partitionDetails?.id}
                        onLoaded={onProductQuantityStatusLoaded}
                        quantity={fields.quantityChange}
                        formatMessage={formatMessageQuantity}
                    />
                </Grid>

                <Grid item xs={12}>
                    <TextField
                        id="notes-field"
                        label={notesLabel}
                        fullWidth
                        value={fields.notes ?? ''}
                        helperText={notesHelperText}
                        inputProps={{
                            maxLength: 256
                        }}
                        required={true}
                        onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => {
                            let notes = event.target.value;
                            handleFieldChange({ notes });
                        }}
                    />
                </Grid>
            </Grid>
        </>
    );
}
